import { parseError } from "@/lib/parse-error";
import { getTimezone } from "@/lib/timezone";

import { DiscountDetail } from "@/types/billing/discount";
import {
  BulkActionResult,
  IssuedDiscountUpdate,
  MemberAwaitingFormEnrollment,
  MemberBillingDateUpdate,
  MemberBulkBillingDateUpdate,
  MemberBulkFreezeRequest,
  MemberBulkRerunBillingRequest,
  MemberBulkStatusUpdate,
  MemberCreditPaymentUpdate,
  MemberDeferredEnrollment,
  MemberDetail,
  MemberFreezeRequest,
  MemberLogDetail,
  MemberManualPayment,
  MemberMerchantAccountUpdate,
  MemberPaymentMethodUpdate,
  MemberPriceUpdate,
  MemberStatusUpdate,
  MemberSwitchPreview,
  MemberSwitchRequest,
} from "@/types/inventory/member";
import {
  MemberBulkPaymentLinkRequest,
  MemberPastDue,
  MemberPaymentLinkRequest,
  MemberPaymentLinkResult,
  MemberPaymentRow,
  MemberTableFilters,
  MemberTableRow,
} from "@/types/inventory/member-collections";
import { Page } from "@/types/pagination";

import { requestData } from "@/api/api-config";

/**
 * Update a member's payment method
 */
export async function updatePaymentMethod(
  update: MemberPaymentMethodUpdate,
): Promise<MemberDetail | null> {
  return await requestData<MemberDetail>(
    "inventory/members/update-payment-method",
    "post",
    {
      json: update,
    },
  );
}

/**
 * Update a member's status (ACTIVE, FROZEN, CANCELLED, etc.)
 */
export async function updateStatus(
  update: MemberStatusUpdate,
): Promise<MemberDetail | null> {
  return await requestData<MemberDetail>(
    "inventory/members/update-status",
    "post",
    {
      json: update,
    },
  );
}

/**
 * Switch a member to a different plan, archiving their current benefits.
 */
export async function switchMembership(
  request: MemberSwitchRequest,
): Promise<MemberDetail | null> {
  return await requestData<MemberDetail>("inventory/members/switch", "post", {
    json: request,
  });
}

/**
 * What switching the member to a plan right now would bill, with and without
 * keeping their current term.
 */
export async function getSwitchPreview(
  memberId: string,
  newMembershipId: string,
): Promise<MemberSwitchPreview | null> {
  return await requestData<MemberSwitchPreview>(
    `inventory/members/${memberId}/switch-preview`,
    "get",
    { searchParams: { newMembershipId } },
  );
}

/**
 * Update a member's next billing date
 */
export async function updateBillingDate(
  update: MemberBillingDateUpdate,
): Promise<MemberDetail | null> {
  return await requestData<MemberDetail>(
    "inventory/members/update-billing-date",
    "post",
    {
      json: update,
    },
  );
}

/**
 * Set or clear the custom recurring price for one member
 */
export async function updateMemberPrice(
  update: MemberPriceUpdate,
): Promise<MemberDetail | null> {
  // Throw rather than resolve null so the dialog can show why it failed.
  return await requestData<MemberDetail>(
    "inventory/members/update-price",
    "post",
    {
      json: update,
    },
    false,
  );
}

/**
 * Update a member's discount (modify quantity or expiry date)
 */
export async function updateDiscount(
  update: IssuedDiscountUpdate,
): Promise<MemberDetail | null> {
  return await requestData<MemberDetail>(
    "inventory/members/update-discount",
    "post",
    {
      json: update,
    },
  );
}

/**
 * Process a manual payment for a member (typically for delinquent members)
 * Throws an Error with the backend error message if the payment fails.
 */
export async function manualPayment(
  payment: MemberManualPayment,
): Promise<MemberDetail> {
  try {
    const result = await requestData<MemberDetail>(
      "inventory/members/manual-payment",
      "post",
      {
        json: payment,
      },
      false, // Don't catch errors - let them propagate for proper error handling
    );
    if (!result) {
      throw new Error("Failed to process payment");
    }
    return result;
  } catch (error: any) {
    // Extract error message from API response and throw a regular Error
    // This provides a better error message and avoids Next.js error overlay issues
    // `parseError`, not `errorData?.detail`: a refused card returns
    // `detail: {code, decline_class, message}`, and interpolating that object
    // into an Error toasted "[object Object]" at staff. `parseError` maps the
    // class to our own copy and falls back to the message, which is the same
    // thing every other decline surface shows.
    let errorMessage = await parseError(error, "Failed to process payment");
    if (!errorMessage && error?.message) {
      errorMessage = error.message;
    }
    throw new Error(errorMessage);
  }
}

/**
 * Update the sold by provider for a member
 */
export async function updateMemberSoldByProvider(
  memberId: string,
  providerId: string | null,
): Promise<boolean> {
  const result = await requestData<boolean>(
    "inventory/members/update-sold-by-provider",
    "post",
    {
      json: {
        member_id: memberId,
        provider_id: providerId,
      },
    },
  );
  return result ?? false;
}

export async function updateMemberMerchantAccount(
  update: MemberMerchantAccountUpdate,
): Promise<MemberDetail | null> {
  return await requestData<MemberDetail>(
    "inventory/members/update-merchant-account",
    "post",
    { json: update },
  );
}

export async function updateMemberCreditPayment(
  update: MemberCreditPaymentUpdate,
): Promise<MemberDetail | null> {
  return await requestData<MemberDetail>(
    "inventory/members/update-credit-payment",
    "post",
    { json: update },
  );
}

/**
 * Freeze a member's membership with optional freeze fee payment
 */
export async function freezeMembership(
  request: MemberFreezeRequest,
): Promise<MemberDetail | null> {
  return await requestData<MemberDetail>("inventory/members/freeze", "post", {
    json: request,
  });
}

/**
 * Get member details by member ID
 */
export async function getMember(
  memberId: string,
): Promise<MemberDetail | null> {
  return await requestData<MemberDetail>(
    `inventory/members/${memberId}`,
    "get",
  );
}

export interface EarlyCancellationFeeInfo {
  feeDue: number;
  // What a later refund of the member's latest billed cycle could return. Cancelling
  // does not refund that cycle; these are shown so staff know before they go looking.
  nonRefundableAmount: number;
  refundableAmount: number;
}

export interface PreActivationEnrollmentMoney {
  outstandingAmount: number;
  paidAmount: number;
}

/**
 * Money in flight on an enrollment that never activated (AWAITING_FORM / PENDING):
 * a declined or settled enrollment charge, or a clearing ACH payin. Returns null when
 * the request fails, so callers can tell "unknown" from a genuine $0 — removing the
 * enrollment refunds none of it, so the difference matters.
 */
export async function getPreActivationMoney(
  memberId: string,
): Promise<PreActivationEnrollmentMoney | null> {
  return await requestData<PreActivationEnrollmentMoney>(
    `inventory/members/${memberId}/pre-activation-money`,
    "get",
  );
}

/**
 * Remove an enrollment that never activated. Cancels the member *and* strips the
 * enrollment intent from any outstanding enrollment charge, so collecting that charge
 * later cannot resurrect the enrollment as a fresh duplicate row.
 */
export async function removeEnrollment(removal: {
  memberId: string;
  comment?: string;
}): Promise<MemberDetail | null> {
  return await requestData<MemberDetail>(
    "inventory/members/remove-enrollment",
    "post",
    { json: removal },
  );
}

/**
 * Fee (in cents) the member would be charged for cancelling now.
 * Returns null when the request fails (requestData swallows errors) so callers can
 * distinguish an unknown fee from a genuine $0 rather than silently skipping the charge.
 */
export async function getEarlyCancellationFee(
  memberId: string,
): Promise<EarlyCancellationFeeInfo | null> {
  return await requestData<EarlyCancellationFeeInfo>(
    `inventory/members/${memberId}/early-cancellation-fee`,
    "get",
  );
}

export async function getMemberLogs(
  memberId: string,
): Promise<MemberLogDetail[] | null> {
  return await requestData<MemberLogDetail[]>(
    `inventory/members/${memberId}/logs`,
    "get",
  );
}

export async function getMemberIssuedDiscounts(
  memberId: string,
): Promise<DiscountDetail[]> {
  const discounts = await requestData<DiscountDetail[]>(
    `inventory/members/${memberId}/discounts`,
    "get",
    undefined,
    false,
  );
  return discounts ?? [];
}

/**
 * Enroll a new member with deferred billing. With `throwOnError`, a refusal
 * (e.g. 409 already a member) throws so the caller can show its reason.
 */
export async function enrollMemberDeferredBilling(
  enrollment: MemberDeferredEnrollment,
  { throwOnError = false }: { throwOnError?: boolean } = {},
): Promise<MemberDetail | null> {
  return await requestData<MemberDetail>(
    "inventory/members/enroll-deferred",
    "post",
    {
      json: enrollment,
    },
    !throwOnError,
  );
}

/**
 * Bulk freeze: applies the same freeze window to each selected member.
 */
export async function bulkFreezeMembers(
  request: MemberBulkFreezeRequest,
): Promise<BulkActionResult | null> {
  return await requestData<BulkActionResult>(
    "inventory/members/bulk-freeze",
    "post",
    { json: request },
  );
}

/**
 * Bulk status update: typically used to deactivate (cancel) selected members.
 */
export async function bulkUpdateStatus(
  request: MemberBulkStatusUpdate,
): Promise<BulkActionResult | null> {
  return await requestData<BulkActionResult>(
    "inventory/members/bulk-update-status",
    "post",
    { json: request },
  );
}

/**
 * Bulk billing date update: sets the same next billing date on each member.
 */
export async function bulkUpdateBillingDate(
  request: MemberBulkBillingDateUpdate,
): Promise<BulkActionResult | null> {
  return await requestData<BulkActionResult>(
    "inventory/members/bulk-update-billing-date",
    "post",
    { json: request },
  );
}

/**
 * Bulk rerun billing: re-attempts payment for delinquent members. Non-delinquent
 * members are skipped (counted, not failed).
 */
export async function bulkRerunBilling(
  request: MemberBulkRerunBillingRequest,
): Promise<BulkActionResult | null> {
  return await requestData<BulkActionResult>(
    "inventory/members/bulk-rerun-billing",
    "post",
    { json: request },
  );
}

/**
 * Save a member while their membership form is completed async.
 */
export async function enrollMemberAwaitingForm(
  enrollment: MemberAwaitingFormEnrollment,
): Promise<MemberDetail | null> {
  return await requestData<MemberDetail>(
    "inventory/members/enroll-awaiting-form",
    "post",
    {
      json: enrollment,
    },
  );
}

/**
 * Members across every plan. Delinquent rows carry what they owe, why the card
 * failed, and when the patient was last sent a payment link. Throws on failure.
 */
export async function listMembersTable(
  filters: MemberTableFilters,
): Promise<Page<MemberTableRow> | null> {
  return await requestData<Page<MemberTableRow>>(
    "inventory/members/table",
    "get",
    {
      searchParams: {
        page: filters.page,
        per_page: filters.perPage,
        ...(filters.status?.length ? { status: filters.status } : {}),
        ...(filters.membershipId?.length
          ? { membership_id: filters.membershipId }
          : {}),
        ...(filters.name ? { name: filters.name } : {}),
        ...(filters.sortBy ? { sort_by: filters.sortBy } : {}),
        ...(filters.sortDirection
          ? { sort_direction: filters.sortDirection }
          : {}),
        ...(filters.nextBillingFrom
          ? { next_billing_from: filters.nextBillingFrom }
          : {}),
        ...(filters.nextBillingTo
          ? { next_billing_to: filters.nextBillingTo }
          : {}),
        ...(filters.hasCard !== undefined ? { has_card: filters.hasCard } : {}),
        tz: getTimezone(),
      },
    },
    // Throw so a failed load shows as an error with a retry, not an empty list.
    false,
  );
}

/** Every charge the membership made, newest first, with payments and declines. */
export async function getMemberPayments(
  memberId: string,
): Promise<MemberPaymentRow[] | null> {
  return await requestData<MemberPaymentRow[]>(
    `inventory/members/${memberId}/payments`,
    "get",
  );
}

/** What a delinquent membership owes and why; null when it isn't delinquent. */
export async function getMemberPastDue(
  memberId: string,
): Promise<MemberPastDue | null> {
  return await requestData<MemberPastDue | null>(
    `inventory/members/${memberId}/past-due`,
    "get",
  );
}

/**
 * Text or email the patient a link that pays only this membership's unpaid
 * charges. Throws with the API's message when it can't be sent.
 */
export async function sendMemberPaymentLink(
  memberId: string,
  request: MemberPaymentLinkRequest,
): Promise<MemberPaymentLinkResult> {
  try {
    const result = await requestData<MemberPaymentLinkResult>(
      `inventory/members/${memberId}/send-payment-link`,
      "post",
      { json: request },
      false,
    );
    if (!result) throw new Error("Failed to send the payment link");
    return result;
  } catch (error) {
    throw new Error(await parseError(error, "Failed to send the payment link"));
  }
}

/** Send each selected member a payment link; members with nothing unpaid are skipped. */
export async function bulkSendMemberPaymentLink(
  request: MemberBulkPaymentLinkRequest,
): Promise<BulkActionResult | null> {
  return await requestData<BulkActionResult>(
    "inventory/members/bulk-send-payment-link",
    "post",
    { json: request },
  );
}
